import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { StdioServerTransport } from '@modelcontextprotocol/sdk/server/stdio.js';
import { registerDataExplorerTools } from './tools/data-explorer.js';
import { registerSecurityTools } from './tools/security.js';
import { registerIsrTools } from './tools/isr.js';
import { registerDashboardTools } from './tools/dashboard.js';
import { registerClarificationTools } from './tools/clarification.js';
import { registerPackageTools } from './tools/packages.js';
import { registerQuestionTools } from './tools/questions.js';
import { registerOrchestrationTools } from './tools/orchestration.js';
import { registerPipelineTools } from './tools/pipeline.js';
import { registerSafeWriteTools } from './tools/safe-write.js';
import { registerAuthTools } from './tools/auth.js';
import { registerIntegrityTools } from './integrity.js';
import { registerArtifactSigningTools } from './artifact-signing.js';
import { registerDomainTools } from './tools/domain.js';
import { registerTypeSchemasTool } from './tools/type-schemas.js';
import { registerValidateYamlFragmentTool } from './tools/validate-yaml-fragment.js';
import { registerProPageTools } from './tools/pages.js';
import { registerGetSchemaTool } from './tools/get-schema.js';
import { registerScaffoldCleanupTools } from './tools/scaffold-cleanup.js';
import { registerContrastTools } from './tools/contrast.js';
import { registerCompileTools } from './tools/compile.js';
import { registerStripLegacyIntegrationsTool } from './tools/strip-legacy-integrations.js';
import { registerEmitterTools } from './tools/emitters.js';
import { registerListSpecsTool } from './tools/list-specs.js';
import { registerConsolidateIntegrationsTool } from './tools/consolidate-integrations.js';
import { loadPipelineGraph } from './tools/pipeline-graph.js';
import { PHASE_ORDER } from './tools/pipeline.js';
import packageJson from '../package.json';
import {
  registerContentTypeTools,
  registerPageTools,
  registerSiteTools,
  registerProjectTools,
  registerGitOpsTools,
  registerBoardTools,
  registerCollectionTools,
  registerIntegrationTools,
  registerComposeTools,
  registerRenderTools,
  registerA11yTools,
  closeBrowser,
} from '@stackwright/mcp/register';

const server = new McpServer({
  name: 'stackwright-pro',
  version: packageJson.version,
});

// Register all Pro tools
registerDataExplorerTools(server); // stackwright_pro_list_entities, stackwright_pro_generate_filter
registerSecurityTools(server); // stackwright_pro_validate_spec, stackwright_pro_add_approved_spec, stackwright_pro_list_approved_specs
registerIsrTools(server); // stackwright_pro_configure_isr, stackwright_pro_configure_isr_batch
registerDashboardTools(server); // stackwright_pro_generate_dashboard, stackwright_pro_generate_detail_page
registerClarificationTools(server); // stackwright_pro_clarify, stackwright_pro_detect_conflict
registerPackageTools(server); // stackwright_pro_setup_packages
registerQuestionTools(server); // stackwright_pro_present_phase_questions
registerOrchestrationTools(server); // stackwright_pro_parse_otter_response, stackwright_pro_save_manifest, stackwright_pro_save_phase_answers, stackwright_pro_read_phase_answers, stackwright_pro_get_otter_name
registerPipelineTools(server); // stackwright_pro_get_pipeline_state, stackwright_pro_set_pipeline_state, stackwright_pro_check_execution_ready, stackwright_pro_list_artifacts, stackwright_pro_write_phase_questions, stackwright_pro_build_specialist_prompt, stackwright_pro_validate_artifact
registerSafeWriteTools(server); // stackwright_pro_safe_write
registerAuthTools(server); // stackwright_pro_configure_auth
registerIntegrityTools(server); // stackwright_pro_verify_otter_integrity
registerArtifactSigningTools(server); // stackwright_pro_verify_artifact_signatures
registerDomainTools(server); // stackwright_pro_list_collections, stackwright_pro_resolve_data_strategy, stackwright_pro_validate_workflow
registerTypeSchemasTool(server); // stackwright_pro_get_type_schemas
registerValidateYamlFragmentTool(server); // stackwright_pro_validate_yaml_fragment
registerProPageTools(server); // stackwright_pro_write_page
registerGetSchemaTool(server); // stackwright_pro_get_schema
registerScaffoldCleanupTools(server); // stackwright_pro_cleanup_scaffold
registerContrastTools(server); // stackwright_pro_check_contrast, stackwright_pro_derive_accessible_palette
registerCompileTools(server); // stackwright_pro_compile_collections, _auth, _integrations, _all, _theme, _site, _pages, _page
registerStripLegacyIntegrationsTool(server); // stackwright_pro_strip_legacy_integrations
registerEmitterTools(server); // stackwright_pro_emit_env_local
registerListSpecsTool(server); // stackwright_pro_list_specs
registerConsolidateIntegrationsTool(server); // stackwright_pro_consolidate_integrations

// --- Compose OSS Stackwright MCP tools onto the Pro server ---
// Architectural principle: Pro is an extension of OSS. Every OSS tool is
// available to Pro otters via this same server, called by their canonical
// `stackwright_*` names (NOT renamed to `stackwright_pro_*`).
registerContentTypeTools(server);
registerPageTools(server);
registerSiteTools(server);
registerProjectTools(server);
registerGitOpsTools(server);
registerBoardTools(server);
registerCollectionTools(server);
registerIntegrationTools(server);
registerComposeTools(server);
registerRenderTools(server);
registerA11yTools(server);

// SIGINT/SIGTERM — clean up Playwright browser pool from OSS render tools.
// Pro now owns this lifecycle when running as the composed MCP server.
process.on('SIGINT', async () => {
  const forceExit = setTimeout(() => process.exit(1), 3000);
  forceExit.unref();
  await closeBrowser();
  process.exit(0);
});
process.on('SIGTERM', async () => {
  const forceExit = setTimeout(() => process.exit(1), 3000);
  forceExit.unref();
  await closeBrowser();
  process.exit(0);
});

async function main() {
  // ── Startup validation: fail fast if otter DAG is malformed ──────────────
  // Throws on cycle, dangling input, or duplicate producer in pipeline declarations.
  // Also validates that PHASE_ORDER is a topologically consistent ordering.
  const pipelineGraph = loadPipelineGraph();
  const phasePosition = new Map(PHASE_ORDER.map((p, i) => [p, i] as const));
  for (const [phase, deps] of Object.entries(pipelineGraph.dependencies)) {
    const phaseIdx = phasePosition.get(phase as (typeof PHASE_ORDER)[number]);
    if (phaseIdx === undefined) continue;
    for (const dep of deps) {
      const depIdx = phasePosition.get(dep as (typeof PHASE_ORDER)[number]);
      if (depIdx !== undefined && depIdx >= phaseIdx) {
        throw new Error(
          `PHASE_ORDER sanity check failed: "${dep}" (position ${depIdx}) must precede ` +
            `"${phase}" (position ${phaseIdx}) but it doesn't. ` +
            `Update PHASE_ORDER in pipeline.ts to reflect the new dependency structure.`
        );
      }
    }
  }

  const transport = new StdioServerTransport();

  // Optional: register services tools if @stackwright-services/mcp is installed.
  // This allows the services otter to use its tools via the single pro MCP server
  // instead of requiring a separate MCP process.
  // NOTE: explicit `: string` annotation — prevents TypeScript from inferring a
  // literal type and attempting to resolve the optional package during DTS build.
  try {
    const servicesRegisterPkg: string = '@stackwright-services/mcp/register';

    const mod = await import(servicesRegisterPkg as any);
    if (typeof mod.registerServicesTools === 'function') {
      mod.registerServicesTools(server);
      console.error('Stackwright Services tools registered on pro MCP');
    }
  } catch {
    // @stackwright-services/mcp not installed — services phase will use fallback behavior
  }

  await server.connect(transport);
  console.error('Stackwright Pro MCP server running on stdio');
}

main().catch((err) => {
  console.error('Fatal:', err);
  process.exit(1);
});
